import { Fragment, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import type { OrgStoryView, StorySectionKey } from "@/lib/orgStory";
import SoftEmpty from "@/components/organization/kit/SoftEmpty";
import SectionPills from "./SectionPills";
import { storySectionAnchor } from "./storyLabels";
import PageBand from "@/components/primitives/PageBand";

/** The data-driven sections that can decide the page's own empty state; the
 * self-fetching ones (team/reviews/news, signed-in page only) never count
 * toward it directly — they must each resolve empty first (see `resolved`
 * below). */
const DATA_SECTION_KEYS: readonly StorySectionKey[] = [
  "overview",
  "offer",
  "growth",
  "operations",
  "footprint",
];

interface OrgStoryProps {
  view: OrgStoryView;
  /** Above the header, inside the page band (e.g. the public page's breadcrumb). */
  topSlot?: ReactNode;
  header: ReactNode;
  /** Desktop sticky decision panel. */
  panel: ReactNode;
  /** Below `lg`, rendered in flow right after the header (e.g. a second decision panel). */
  mobileAside?: ReactNode;
  mobileBar?: ReactNode;
  /** The already-filtered candidate sections, in reading order. */
  sections: StorySectionKey[];
  renderSection: (key: StorySectionKey, ctx: { hidden: boolean; onResolved: (has: boolean) => void }) => ReactNode;
  /**
   * Scroll this section into view once, the first moment it's actually
   * visible: immediately for a data section (always a candidate from
   * mount), or once a self-fetching section resolves with content. Never
   * fires if the section resolves empty. Used when returning from a news
   * article (spec follow-up, 2026-09-24).
   */
  initialSection?: StorySectionKey;
}

/**
 * The org story's layout (spec 2026-09-24 §5.1/§6.3): header, an optional
 * mobile-only aside, a two-column body (sections + a sticky decision panel),
 * and an optional mobile bottom bar. Presentational only — it owns just the
 * self-fetching sections' resolution bookkeeping and delegates everything
 * else to `renderSection`.
 *
 * Self-fetching sections (team/reviews/news) don't know whether they have
 * content until their own fetch settles, so a key here is either unresolved
 * (no entry — still loading, no pill yet), resolved with content (`true` —
 * pill shown, section visible), or resolved empty (`false` — pill dropped,
 * section `hidden`). Data sections always get a pill; only a self-fetching
 * key's own `onResolved` call can add or remove one.
 */
export default function OrgStory({
  view,
  topSlot,
  header,
  panel,
  mobileAside,
  mobileBar,
  sections,
  renderSection,
  initialSection,
}: OrgStoryProps) {
  const [resolved, setResolved] = useState<ReadonlyMap<StorySectionKey, boolean>>(() => new Map());

  // Only the signed-in page has self-fetching sections (Team, Reviews and
  // News load their own data). On the public page News comes with the RPC
  // payload and Team/Reviews never render, so every section is data-driven.
  const isDataKey = (key: StorySectionKey) =>
    view.audience !== "signedIn" || DATA_SECTION_KEYS.includes(key);

  const sectionsKey = sections.join("|");

  // Prune entries for keys no longer in `sections`, so a self-fetching key
  // that leaves and later returns starts over rather than reusing a stale
  // resolution from its previous appearance.
  useEffect(() => {
    setResolved((prev) => {
      let changed = prev.size !== sections.filter((k) => prev.has(k)).length;
      const next = new Map<StorySectionKey, boolean>();
      for (const key of sections) {
        if (prev.has(key)) next.set(key, prev.get(key)!);
      }
      if (!changed) {
        for (const key of next.keys()) {
          if (prev.get(key) !== next.get(key)) changed = true;
        }
      }
      return changed ? next : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectionsKey]);

  const onResolvedByKey = useMemo(() => {
    const map = new Map<StorySectionKey, (has: boolean) => void>();
    for (const key of sections) {
      map.set(key, (has: boolean) => {
        setResolved((prev) => {
          if (prev.get(key) === has) return prev; // no change: bail out
          const next = new Map(prev);
          next.set(key, has);
          return next;
        });
      });
    }
    return map;
    // `sectionsKey` stands in for `sections`, whose array identity changes every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectionsKey]);

  const visibleSections = sections.filter((k) => isDataKey(k) || resolved.get(k) === true);

  // Scroll to `initialSection` exactly once, the first moment it's visible.
  // A data key is visible from the first render, so this fires on mount; a
  // self-fetching key (e.g. news) isn't visible until it resolves with
  // content, so this waits for `resolved` to say so — and never fires at
  // all if it resolves empty, because it then never joins `visibleSections`.
  const scrolledToInitialSectionRef = useRef(false);
  const visibleSectionsKey = visibleSections.join("|");
  useEffect(() => {
    if (!initialSection || scrolledToInitialSectionRef.current) return;
    if (!visibleSections.includes(initialSection)) return;
    scrolledToInitialSectionRef.current = true;
    document.getElementById(storySectionAnchor(initialSection))?.scrollIntoView({ block: "start" });
    // `visibleSectionsKey` stands in for `visibleSections`, whose array identity changes every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialSection, visibleSectionsKey]);

  const hasDataSection = sections.some(isDataKey);
  const selfFetchKeys = sections.filter((k) => !isDataKey(k));
  const allSelfFetchResolvedEmpty = selfFetchKeys.every((k) => resolved.get(k) === false);
  const showEmpty = !hasDataSection && allSelfFetchResolvedEmpty;
  const emptyTitle = `${view.identity.name} hasn't published their story yet.`;

  return (
    // The band spans the full content width; the story column stays centered above it.
    <div className="relative">
      <PageBand size="sm" />
      <div className="relative mx-auto max-w-[1140px] px-4 pt-4 pb-10 sm:px-[30px] sm:pt-5">
      {topSlot}
      {header}

      {mobileAside && (
        <div
          role="region"
          aria-label={`About joining ${view.identity.name}`}
          className="mt-4 flex flex-col gap-4 lg:hidden"
        >
          {mobileAside}
        </div>
      )}

      <div className="mt-6 grid gap-8 lg:grid-cols-[minmax(0,1fr)_300px]">
        <div className="min-w-0">
          <SectionPills sections={visibleSections} />

          {showEmpty && <SoftEmpty title={emptyTitle} />}

          <div className="mt-4 flex flex-col gap-12">
            {sections.map((key) => (
              <Fragment key={key}>
                {renderSection(key, {
                  // A self-fetching section stays hidden until it resolves
                  // WITH content — while loading (no entry yet) it must add
                  // no visible gap or landmark, not just avoid a wrong one.
                  // Data sections, and every section on the public page, are
                  // never hidden.
                  hidden: !isDataKey(key) && resolved.get(key) !== true,
                  onResolved: onResolvedByKey.get(key)!,
                })}
              </Fragment>
            ))}
          </div>
        </div>

        <aside aria-label={`About joining ${view.identity.name}`} className="hidden lg:block">
          <div className="sticky top-[80px] max-h-[calc(100vh-96px)] overflow-y-auto p-1">{panel}</div>
        </aside>
      </div>

      {mobileBar}
      </div>
    </div>
  );
}
